// IMPORTS
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AnimatedText from './AnimatedText';
import '../../Styles/nav.css';

// CONSTANTS
const NAV_LINKS = [
  { path: '/', label: 'হোম পেইজ', icon: '🏠' },
  { path: '/ambulance', label: 'এ্যাম্বুলেন্স', icon: '🚑' },
  { path: '/emergency', label: 'এমারজেন্সি নম্বর', icon: '🚨' },
  { path: '/blood-bank', label: 'ব্লাড ব্যাংক', icon: '🩸' },
  { path: '/clinics', label: 'ক্লিনিক ও হাসপাতাল', icon: '🏥' },
  { path: '/diagnostics', label: 'ডায়াগনস্টিক সেন্টার', icon: '🔬' },
  { path: '/pharmacy', label: 'ফার্মেসি', icon: '💊' },
  { path: '/feedback', label: 'ফিডব্যাক', icon: '💬' },
  { path: '/contact-us', label: 'যোগাযোগ', icon: '📞' },
  { path: '/about', label: 'আমাদের সম্পর্কে', icon: 'ℹ️' },
  { path: '/disclaimer', label: 'দায়মুক্তি', icon: '⚠️' },
];

const ADMIN_LINKS = [
  { path: '/add-content', label: 'কন্টেন্ট যোগ করুন', icon: '📝' },
  { path: '/add-doctor', label: 'ডাক্তার যোগ করুন', icon: '➕' },
  { path: '/update-doctor', label: 'ডাক্তার আপডেট', icon: '✏️' },
];

// COMPONENT
const Nav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);
  const hamburgerRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const hamburgerButton = hamburgerRef.current;

      // Don't close if clicking on hamburger button
      if (hamburgerButton && hamburgerButton.contains(event.target)) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Add scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsDropdownOpen(false);
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  return (
    <nav
      className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-gradient-to-r from-gray-900 via-black to-gray-900 shadow-2xl py-3'
          : 'bg-gradient-to-r from-gray-800 via-black to-gray-800 py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Logo/Brand */}
          <div className="flex items-center space-x-3 z-50">
            <Link
              to="/"
              className="flex items-center space-x-2 hover:opacity-90 transition-opacity"
            >
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-2 rounded-full shadow-lg">
                <span className="text-white font-bold text-xl">🏥</span>
              </div>
              <div className="block">
                <AnimatedText />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-all duration-200 nav-item-float ${
                  location.pathname === link.path
                    ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white font-semibold shadow-lg active-glow'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white border border-transparent hover:border-gray-700 hover-lift'
                }`}
              >
                <span className="text-sm">{link.icon}</span>
                <span className="text-sm font-medium">{link.label}</span>
              </Link>
            ))}
          </div>

          {/* User Section */}
          <div className="flex items-center space-x-4 z-50">
            {/* User Profile Dropdown */}
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={toggleDropdown}
                  className="flex items-center space-x-2 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 text-white px-4 py-2 rounded-full transition-all duration-200 border border-gray-700 shadow-lg hover-lift"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow-md icon-pulse">
                    <span className="font-bold text-white text-sm">
                      {user.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="hidden md:block font-medium">{user.name}</span>
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isDropdownOpen ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-gradient-to-b from-gray-900 to-black rounded-xl shadow-2xl overflow-hidden border border-gray-800 dropdown-enter">
                    {/* User Info */}
                    <div className="p-4 bg-gradient-to-r from-gray-800 to-gray-900 text-white border-b border-gray-800">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow-md">
                          <span className="text-xl">👨‍⚕️</span>
                        </div>
                        <div>
                          <p className="font-bold text-lg text-gradient-gold">{user.name}</p>
                          <p className="text-sm text-gray-300 capitalize">{user.role}</p>
                          <p className="text-xs text-gray-400 truncate">{user.email}</p>
                        </div>
                      </div>
                    </div>

                    {/* Admin Links */}
                    {user.role === 'admin' && (
                      <div className="border-b border-gray-800">
                        <div className="p-2">
                          <p className="text-xs text-gray-400 px-3 py-1 uppercase font-semibold">Admin Panel</p>
                          {ADMIN_LINKS.map((link) => (
                            <Link
                              key={link.path}
                              to={link.path}
                              className="flex items-center space-x-3 px-3 py-2.5 hover:bg-gray-800 rounded-lg transition-colors text-gray-200 hover-scale"
                            >
                              <span className="text-lg">{link.icon}</span>
                              <span className="font-medium">{link.label}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="p-2">
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex items-center space-x-3 w-full px-3 py-2.5 text-white hover:bg-gradient-to-r hover:from-red-900 hover:to-red-800 rounded-lg transition-all duration-200 font-medium bg-gradient-to-r from-red-800 to-red-700 hover-lift focus-gold"
                      >
                        <span className="text-lg">🚪</span>
                        <span>লগআউট</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-6 py-2.5 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-200 border border-amber-700 hover-lift shimmer-gold"
              >
                লগইন
              </Link>
            )}

            {/* Mobile Menu Button - Fixed positioning */}
            <button
              ref={hamburgerRef}
              type="button"
              onClick={toggleMenu}
              className="lg:hidden relative bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 p-2 rounded-lg transition-all duration-200 border border-gray-700 shadow-lg hover-lift z-50"
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              <div className="w-6 h-6 relative flex items-center justify-center">
                <span
                  className={`absolute h-0.5 w-6 bg-gradient-to-r from-amber-400 to-orange-400 transition-all duration-300 transform ${
                    isOpen ? 'rotate-45 translate-y-0' : '-translate-y-2'
                  }`}
                />
                <span
                  className={`absolute h-0.5 w-6 bg-gradient-to-r from-amber-400 to-orange-400 transition-all duration-300 ${
                    isOpen ? 'opacity-0' : 'opacity-100'
                  }`}
                />
                <span
                  className={`absolute h-0.5 w-6 bg-gradient-to-r from-amber-400 to-orange-400 transition-all duration-300 transform ${
                    isOpen ? '-rotate-45 translate-y-0' : 'translate-y-2'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation - Fixed positioning and styling */}
        <div
          ref={menuRef}
          className={`lg:hidden fixed top-0 left-0 right-0 h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 shadow-2xl transition-all duration-300 overflow-y-auto z-40 pt-20 ${
            isOpen
              ? 'opacity-100 visible translate-x-0'
              : 'opacity-0 invisible translate-x-full'
          }`}
        >
          <div className="container mx-auto px-4 py-6 h-full pb-32">
            {/* Mobile Nav Links */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all border hover-lift ${
                    location.pathname === link.path
                      ? 'bg-gradient-to-r from-amber-700 to-orange-700 text-white border-amber-600 shadow-lg active-glow'
                      : 'bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 text-gray-300 hover:text-white border-gray-700 hover:border-amber-600/50'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <span className="text-2xl mb-2">{link.icon}</span>
                  <span className="text-xs font-medium text-center">{link.label}</span>
                </Link>
              ))}
            </div>

            {/* Admin Links for Mobile */}
            {user?.role === 'admin' && (
              <div className="mb-6">
                <p className="text-gray-400 text-sm font-semibold mb-3 px-2">Admin Panel</p>
                <div className="grid grid-cols-2 gap-3">
                  {ADMIN_LINKS.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="flex items-center space-x-3 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-amber-800 hover:to-orange-800 text-white p-4 rounded-xl transition-all duration-200 border border-gray-700 hover:border-amber-700 shadow-md hover-lift"
                      onClick={() => setIsOpen(false)}
                    >
                      <span className="text-xl">{link.icon}</span>
                      <span className="text-sm font-medium">{link.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* User Info Mobile */}
            {user ? (
              <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-700 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow-md icon-pulse">
                      <span className="font-bold text-white">
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-semibold">{user.name}</p>
                      <p className="text-gray-300 text-sm capitalize">{user.role}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="bg-gradient-to-r from-red-700 to-red-800 hover:from-red-800 hover:to-red-900 text-white px-4 py-2.5 rounded-lg font-medium transition-all duration-200 border border-red-800 shadow-md hover-lift"
                  >
                    লগআউট
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8 py-3.5 rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-200 border border-amber-700 hover-lift shimmer-gold"
                  onClick={() => setIsOpen(false)}
                >
                  <span className="text-lg">🔐</span>
                  <span>লগইন করুন</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Backdrop for mobile menu - Now only shows when menu is open */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/80 z-30 backdrop-blur-md"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </nav>
  );
};

// EXPORT
export default Nav;
